import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  StarIcon,
  CheckCircleIcon,
  UserIcon,
  BriefcaseIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { submitFeedback } from '../../services/shareService';

const FeedbackForm = ({ shareToken, onSubmitted, className = '' }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Form state
  const [overallRating, setOverallRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [quickFeedback, setQuickFeedback] = useState({
    bestSection: '',
    needsImprovement: '',
    wouldInterview: null
  });
  const [generalComments, setGeneralComments] = useState('');
  const [reviewerName, setReviewerName] = useState('');
  const [reviewerRole, setReviewerRole] = useState('');

  const sections = [
    { id: 'summary', label: 'Summary' },
    { id: 'experience', label: 'Experience' },
    { id: 'skills', label: 'Skills' },
    { id: 'education', label: 'Education' },
    { id: 'overall_format', label: 'Overall Format' }
  ];

  const handleSubmit = async () => {
    if (overallRating === 0) return;

    setLoading(true);
    try {
      const response = await submitFeedback(shareToken, {
        overallRating,
        quickFeedback: {
          ...quickFeedback,
          bestSection: quickFeedback.bestSection || undefined,
          needsImprovement: quickFeedback.needsImprovement || undefined
        },
        generalComments: generalComments.trim() || undefined,
        reviewerName: reviewerName.trim() || 'Anonymous',
        reviewerRole: reviewerRole.trim() || undefined
      });

      if (response.success) {
        setSubmitted(true);
        if (onSubmitted) onSubmitted();
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className={`bg-white rounded-xl border border-gray-200 p-8 text-center ${className}`}>
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircleIcon className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Thank You!
        </h3>
        <p className="text-gray-600 mb-6">
          Your feedback has been submitted and will help improve this resume.
        </p>
        <div className="p-4 bg-primary-50 rounded-lg">
          <p className="text-sm text-primary-800 mb-2">
            Want to build your own professional resume?
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Try Kwikly Free
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl border border-gray-200 overflow-hidden ${className}`}>
      {/* Header */}
      <div className="p-6 border-b border-gray-100">
        <h3 className="text-lg font-semibold text-gray-900">
          Share Your Feedback
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Help improve this resume with your honest feedback
        </p>
      </div>

      {/* Progress steps */}
      <div className="px-6 py-3 bg-gray-50 border-b border-gray-100">
        <div className="flex items-center gap-2">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`
                w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium
                ${step >= s
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-200 text-gray-500'
                }
              `}>
                {s}
              </div>
              {s < 3 && (
                <div className={`w-12 h-0.5 ${step > s ? 'bg-primary-600' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Step 1: Overall Rating */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Overall Rating *
              </label>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setOverallRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    {(hoverRating || overallRating) >= star ? (
                      <StarIconSolid className="w-8 h-8 text-yellow-400" />
                    ) : (
                      <StarIcon className="w-8 h-8 text-gray-300" />
                    )}
                  </button>
                ))}
                <span className="ml-2 text-sm text-gray-500">
                  {overallRating > 0 && ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][overallRating]}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Would you interview this candidate?
              </label>
              <div className="flex gap-3">
                <button
                  onClick={() => setQuickFeedback({ ...quickFeedback, wouldInterview: true })}
                  className={`
                    flex-1 py-3 px-4 rounded-lg border-2 text-sm font-medium transition-all
                    ${quickFeedback.wouldInterview === true
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }
                  `}
                >
                  Yes, I would
                </button>
                <button
                  onClick={() => setQuickFeedback({ ...quickFeedback, wouldInterview: false })}
                  className={`
                    flex-1 py-3 px-4 rounded-lg border-2 text-sm font-medium transition-all
                    ${quickFeedback.wouldInterview === false
                      ? 'border-red-500 bg-red-50 text-red-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }
                  `}
                >
                  Probably not
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Section Feedback */}
        {step === 2 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Strongest Section
              </label>
              <div className="flex flex-wrap gap-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setQuickFeedback({ ...quickFeedback, bestSection: section.id })}
                    className={`
                      px-4 py-2 rounded-full text-sm font-medium transition-all
                      ${quickFeedback.bestSection === section.id
                        ? 'bg-green-100 text-green-700 ring-2 ring-green-500'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }
                    `}
                  >
                    {section.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Needs Most Improvement
              </label>
              <div className="flex flex-wrap gap-2">
                {[...sections, { id: 'length', label: 'Length' }, { id: 'formatting', label: 'Formatting' }].map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setQuickFeedback({ ...quickFeedback, needsImprovement: section.id })}
                    className={`
                      px-4 py-2 rounded-full text-sm font-medium transition-all
                      ${quickFeedback.needsImprovement === section.id
                        ? 'bg-amber-100 text-amber-700 ring-2 ring-amber-500'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }
                    `}
                  >
                    {section.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Comments (optional)
              </label>
              <textarea
                value={generalComments}
                onChange={(e) => setGeneralComments(e.target.value)}
                placeholder="What specific improvements would you suggest?"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              />
            </div>
          </div>
        )}

        {/* Step 3: About You */}
        {step === 3 && (
          <div className="space-y-5">
            <p className="text-sm text-gray-500">
              Optional: Help the resume owner understand your perspective
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <UserIcon className="w-4 h-4 inline mr-1" />
                Your Name
              </label>
              <input
                type="text"
                value={reviewerName}
                onChange={(e) => setReviewerName(e.target.value)}
                placeholder="Anonymous"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <BriefcaseIcon className="w-4 h-4 inline mr-1" />
                Your Role/Title
              </label>
              <input
                type="text"
                value={reviewerRole}
                onChange={(e) => setReviewerRole(e.target.value)}
                placeholder="e.g., HR Manager, Recruiter, Engineering Lead"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between p-6 border-t border-gray-100 bg-gray-50">
        {step > 1 ? (
          <button
            onClick={() => setStep(step - 1)}
            className="px-4 py-2 text-gray-600 hover:text-gray-900"
          >
            Back
          </button>
        ) : (
          <div />
        )}

        {step < 3 ? (
          <button
            onClick={() => setStep(step + 1)}
            disabled={step === 1 && overallRating === 0}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Continue
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Submitting...' : 'Submit Feedback'}
          </button>
        )}
      </div>
    </div>
  );
};

FeedbackForm.propTypes = {
  shareToken: PropTypes.string.isRequired,
  onSubmitted: PropTypes.func,
  className: PropTypes.string
};

export default FeedbackForm;
